import { useMemo } from 'react';

// Constant for the representation of omitted pages in the pagination component
export const DOTS = '...';

// Function to generate an array of numbers within a range
const range = (start, end) => {
  let length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

// Custom hook to generate pagination range
export const usePagination = ({
  totalCount, // Total number of items
  pageSize, // Number of items per page
  siblingCount = 1, // Number of pages to show on either side of the current page
  currentPage // Current page number
}) => {
  // Use useMemo for performance optimization
  const paginationRange = useMemo(() => {
    // Calculate the total number of pages
    const totalPageCount = Math.ceil(totalCount / pageSize);

    // Calculate the total number of page numbers to show in the pagination component
    const totalPageNumbers = siblingCount + 5;

    // If the total number of pages is less than the total page numbers, return the range [1..totalPageCount]
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    // Calculate the indices of the sibling pages
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    // Determine whether to show dots for hidden pages on the left and right
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    // Always show the first and last pages
    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    // Generate the range of page numbers to show in different scenarios
    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      );
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, ...middleRange, DOTS, lastPageIndex];
    }
  }, [totalCount, pageSize, siblingCount, currentPage]); // Dependencies of the effect

  return paginationRange;
};